import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { ClipLoader } from "react-spinners"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "border bg-primary text-primary-foreground shadow hover:bg-primary/90",
                destructive:
                    "border bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
                outline:
                    "border border-primary bg-background shadow-sm text-primary hover:bg-primary/10",
                secondary:
                    "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
                ghost: "hover:bg-transparent",
                link: "text-primary underline-offset-4 hover:underline",
                "in-progress": "bg-in-progress/20 text-in-progress-foreground",
            },
            size: {
                default: "h-14 px-4 py-2",
                sm: "h-8 px-3 text-xs font-normal",
                lg: "h-10 px-8",
                icon: "h-9 w-9",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size">,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean,
    showLoader?: boolean,
    loadingText?: string,
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
    className,
    variant,
    size,
    loadingText,
    showLoader = true,
    children,
    asChild = false,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button"

    const renderChildren = () => {
        if (showLoader && props.disabled) {
            return (
                <>
                    <ClipLoader color="text-inherit" size={15} />
                    {loadingText ? loadingText : children}
                </>
            )
        }
        return children
    }
    return (
        <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            suppressHydrationWarning
            {...props}
        >
            {renderChildren()}
        </Comp>
    )
}
)
Button.displayName = "Button"

export { Button, buttonVariants }
